<template>
	<v-card outlined rounded="lg" width="100%" class="debt-visit-card mx-auto">
		<v-card-text class="px-3 pt-3 pb-2">
			<div class="d-flex justify-space-between align-center">
				<span class="text-h6 font-weight-bold green--text text--darken-2">
					{{ visit.entityName }}
				</span>
				<v-chip
					v-if="visit.entityRegionName"
					x-small
					color="orange lighten-5"
					text-color="orange darken-2"
					class="font-weight-medium"
				>
					{{ visit.entityRegionName }}
				</v-chip>
			</div>

			<div class="d-flex align-center grey--text text--darken-1 mb-2">
				<span class="text-caption">{{ formattedDate }}</span>
				<v-icon size="15" class="ms-1" color="grey darken-1">mdi-calendar-blank-outline</v-icon>
			</div>

			<div class="debt-visit-card__row">
				<span class="debt-visit-card__label">{{ $T('inputs.paid-amount') }}</span>
				<span>{{ $options.filters.formatNumber(currentPaidAmount, 3) }} {{ $T('currency') }}</span>
			</div>

			<div class="debt-visit-card__row">
				<span class="debt-visit-card__label">{{ $T('inputs.remaining-amount') }}</span>
				<span class="font-weight-bold error--text">{{ $options.filters.formatNumber(remainingAmountForDebit, 3) }} {{ $T('currency') }}</span>
			</div>
		</v-card-text>

		<div v-if="remainingAmountForDebit > 0" class="px-3 pb-3">
			<v-btn text small color="primary" class="pa-0" @click="openPaymentDialog">
				{{ $T('inputs.add-payment') }}
			</v-btn>
		</div>

		<v-dialog v-model="paymentDialog" max-width="560">
			<v-card>
				<v-toolbar flat color="primary" height="50" dark>
					<v-spacer/>
					{{ $T('inputs.add-payment') }}
					<v-spacer/>
				</v-toolbar>

				<v-card-text class="pt-4">
					<v-sheet outlined rounded class="pa-3 payment-summary">
						<div class="d-flex justify-space-between align-center mb-2">
							<span class="text-body-2">{{ $T('inputs.paid-amount') }}</span>
							<span class="font-weight-bold">{{ $options.filters.formatNumber(currentPaidAmount, 3) }}</span>
						</div>
						<div class="d-flex justify-space-between align-center mb-2">
							<span class="text-body-2">{{ $T('inputs.remaining-amount') }}</span>
							<span class="font-weight-bold error--text">{{ $options.filters.formatNumber(remainingAmountForDebit, 3) }}</span>
						</div>
						<div class="d-flex justify-space-between align-center">
							<span class="text-body-2">{{ $T('inputs.paid-amount-after-payment') }}</span>
							<span class="font-weight-bold success--text">{{ $options.filters.formatNumber(paidAmountAfterNewPayment, 3) }}</span>
						</div>
					</v-sheet>

					<v-text-field
						v-model.number="additionalPaymentAmount"
						type="number"
						dense
						class="mt-3"
						hide-spin-buttons
						:label="$T('inputs.new-payment-amount')"
						:rules="[
							val => Number(val || 0) > 0 || $T('validation-messages.number'),
							val => Number(val || 0) <= +remainingAmountForDebit || $T('messages.payment-amount-exceeds-remaining')
						]"
					/>
				</v-card-text>

				<v-card-actions>
					<v-spacer/>
					<v-btn text color="success" :loading="paymentUpdateLoading" :disabled="paymentUpdateLoading" @click="applyAdditionalPayment">
						{{ $T('save') }}
					</v-btn>
					<v-btn text color="grey darken-1" :disabled="paymentUpdateLoading" @click="paymentDialog = false">
						{{ $T('close') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-card>
</template>

<script>
export default {
	name: 'DebtVisitCard',

	props: {
		visit: {
			type: Object,
			required: true
		}
	},

	data() {
		return {
			paymentDialog: false,
			additionalPaymentAmount: null,
			paymentUpdateLoading: false
		}
	},

	computed: {
		formattedDate() {
			return window.moment(this.visit.createdAt).format('DD MMMM YYYY')
		},

		visitTotal() {
			return (this.visit.materialAmounts || []).reduce((sum, item) => {
				const amount = Number(item?.amount || 0)
				const unitPrice = Number(item?.sellPrice || item?.price || 0)
				const discount = Number(item?.priceDiscount || item?.discount || 0)
				const gross = amount * unitPrice
				return sum + (gross - (discount / 100) * gross)
			}, 0)
		},

		currentPaidAmount() {
			return Number(this.visit.amountReceived || 0)
		},

		remainingAmountForDebit() {
			const remaining = this.visitTotal - this.currentPaidAmount
			return remaining > 0 ? remaining : 0
		},

		paidAmountAfterNewPayment() {
			return this.currentPaidAmount + Number(this.additionalPaymentAmount || 0)
		}
	},

	methods: {
		openPaymentDialog() {
			this.additionalPaymentAmount = null
			this.paymentDialog = true
		},

		applyAdditionalPayment() {
			const paymentToAdd = Number(this.additionalPaymentAmount || 0)
			if (!paymentToAdd) return

			if (paymentToAdd > this.remainingAmountForDebit) {
				this.$eventBus.$emit('show-snackbar', this.$T('messages.payment-amount-exceeds-remaining'), 'info')
				return
			}

			const amountReceived = this.currentPaidAmount + paymentToAdd

			this.paymentUpdateLoading = true
			this.$store.dispatch('debtVisits/modifyAmountReceived', { id: this.visit.id, amountReceived })
				.then(() => {
					this.$eventBus.$emit('show-snackbar', this.$T('Visits.visit-is-updated'), 'success')
					this.$emit('on-payment-updated')
					this.paymentDialog = false
					this.additionalPaymentAmount = null
				})
				.finally(() => {
					this.paymentUpdateLoading = false
				})
		}
	}
}
</script>

<style scoped>
.debt-visit-card__row {
	display: flex;
	justify-content: space-between;
	align-items: center;
	font-size: .875rem;
	margin-bottom: .25rem;
}

.debt-visit-card__label {
	color: rgba(0, 0, 0, .6);
}
</style>
